import isEqual from "lodash/isEqual";
import type { CSSProperties, RefObject } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { useTheme } from "styled-components/macro";

type UsePortalPopoverPositionOptions = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement>;
  /** Must wrap its content in a single child element (see `measureNaturalHeight`). */
  popoverRef: RefObject<HTMLElement>;
  /** Keeps the popover inside the viewport's left/right edges. */
  popoverWidth: number;
  /**
   * Called when the trigger leaves the viewport or is clipped out of view by
   * a scrolling ancestor, so the host can close.
   */
  onTriggerHidden: () => void;
};

function getClippingAncestors(element: HTMLElement): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let node = element.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflow, overflowX, overflowY } = window.getComputedStyle(node);
    if (
      /auto|scroll|hidden|clip/.test(`${overflow} ${overflowX} ${overflowY}`)
    ) {
      ancestors.push(node);
    }
    node = node.parentElement;
  }
  return ancestors;
}

// Zero-size rects come from elements that aren't laid out (e.g. jsdom), which
// can't meaningfully clip or be clipped.
const isUnmeasured = (rect: DOMRect) => rect.width === 0 && rect.height === 0;

function isClippedOut(rect: DOMRect, clipAncestors: HTMLElement[]): boolean {
  if (rect.bottom < 0 || rect.top > window.innerHeight) return true;
  if (isUnmeasured(rect)) return false;
  return clipAncestors.some((ancestor) => {
    const clip = ancestor.getBoundingClientRect();
    if (isUnmeasured(clip)) return false;
    return (
      rect.bottom <= clip.top ||
      rect.top >= clip.bottom ||
      rect.right <= clip.left ||
      rect.left >= clip.right
    );
  });
}

// Natural height = the single content child's offsetHeight plus the
// popover's own vertical padding/border, so it's the same whether or not the
// popover is currently clamped (no clamp toggling at the boundary). Avoids
// scrollHeight, which counts absolutely positioned descendants hanging past
// the popover (e.g. NumericFilterInput's open operator menu), and never
// touches the popover's max-height, since lifting it to measure resets a
// clamped popover's scroll position.
function measureNaturalHeight(popover: HTMLElement): number {
  const content = popover.firstElementChild;
  if (!(content instanceof HTMLElement)) return popover.offsetHeight;
  const { paddingTop, paddingBottom, borderTopWidth, borderBottomWidth } =
    window.getComputedStyle(popover);
  const frame = [paddingTop, paddingBottom, borderTopWidth, borderBottomWidth]
    .map((value) => parseFloat(value) || 0)
    .reduce((sum, value) => sum + value, 0);
  return content.offsetHeight + frame;
}

/**
 * Viewport-relative (`position: fixed`) placement for a filter popover that
 * is portalled out of its trigger's DOM subtree — into Filters.tsx's
 * `#filter-dropdown-portal` or `document.body` — so scrolling/overflow
 * ancestors (e.g. TablePageLayout's wrappers around a collection table's
 * filter row) can't clip it.
 *
 * Opens below the trigger, flips above when only that side fits, and
 * otherwise clamps to the roomier side with a scrolling max-height, since a
 * fixed popover past the viewport edge can't be scrolled into view.
 */
export function usePortalPopoverPosition({
  isOpen,
  triggerRef,
  popoverRef,
  popoverWidth,
  onTriggerHidden,
}: UsePortalPopoverPositionOptions): CSSProperties {
  const theme = useTheme();
  const gap = theme.resolveSizeToNumber("spacing_xs");
  const edgeMargin = theme.resolveSizeToNumber("spacing_lg");
  const [position, setPosition] = useState<CSSProperties>({ top: 0, left: 0 });
  const onTriggerHiddenRef = useRef(onTriggerHidden);
  onTriggerHiddenRef.current = onTriggerHidden;

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    if (!isOpen || !trigger) return;
    const clipAncestors = getClippingAncestors(trigger);

    const updatePosition = () => {
      const rect = trigger.getBoundingClientRect();
      if (isClippedOut(rect, clipAncestors)) {
        onTriggerHiddenRef.current();
        return;
      }

      const viewportHeight = window.innerHeight;
      const preferredLeft =
        rect.left + popoverWidth > window.innerWidth
          ? rect.right - popoverWidth
          : rect.left;
      const left = Math.max(
        edgeMargin,
        Math.min(preferredLeft, window.innerWidth - popoverWidth - edgeMargin)
      );
      const popover = popoverRef.current;
      const height = popover ? measureNaturalHeight(popover) : 0;
      const spaceBelow = viewportHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;

      const below: CSSProperties = {
        left,
        top: rect.bottom,
        bottom: "auto",
        marginTop: gap,
        marginBottom: 0,
      };
      const above: CSSProperties = {
        left,
        top: "auto",
        bottom: viewportHeight - rect.top,
        marginTop: 0,
        marginBottom: gap,
      };

      let next: CSSProperties;
      if (height <= spaceBelow) {
        next = below;
      } else if (height <= spaceAbove) {
        next = above;
      } else {
        const openBelow = spaceBelow >= spaceAbove;
        next = {
          ...(openBelow ? below : above),
          maxHeight: openBelow ? spaceBelow : spaceAbove,
          overflowY: "auto",
          boxSizing: "border-box",
        };
      }

      setPosition((prev) => (isEqual(prev, next) ? prev : next));
    };

    // Leading-edge update keeps the popover pinned to its trigger on the
    // first scroll event; later events in the same frame coalesce into one
    // trailing update.
    let frame: number | null = null;
    let pending = false;
    const scheduleUpdate = () => {
      if (frame !== null) {
        pending = true;
        return;
      }
      updatePosition();
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (pending) {
          pending = false;
          updatePosition();
        }
      });
    };

    // The capture-phase listener also hears the clamped popover scrolling its
    // own content, which doesn't move it.
    const handleScroll = (event: Event) => {
      if (
        event.target instanceof Node &&
        popoverRef.current?.contains(event.target)
      ) {
        return;
      }
      scheduleUpdate();
    };

    updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", scheduleUpdate);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(scheduleUpdate);
    const popover = popoverRef.current;
    if (popover) {
      resizeObserver?.observe(popover);
      // A clamped popover's own box doesn't grow with its content.
      if (popover.firstElementChild) {
        resizeObserver?.observe(popover.firstElementChild);
      }
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver?.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [isOpen, gap, edgeMargin, popoverWidth, triggerRef, popoverRef]);

  return { ...position, position: "fixed", pointerEvents: "auto" };
}
